import Phaser from "phaser";
import { generateCrestFromId, CREST_CHARGE_NAMES } from "../assets/generateTextures";

/** First coa-* index that is a standalone portrait avatar (1..14 are crest charge silhouettes). */
export const COA_PORTRAIT_START = 15;
export const COA_COUNT = 30;

/**
 * Splits an avatar ID into the clean texture key/ID and the equipped title (if any).
 * Returns { id: string | null, title: string | null }
 */
export function parseAvatarId(avatarId: string | null | undefined): { id: string | null, title: string | null } {
  if (!avatarId) return { id: null, title: null };
  const parts = avatarId.split(":");
  return {
    id: parts[0] || null,
    title: parts[1] || null
  };
}

/**
 * Returns true if the given avatarId represents a character portrait
 * (as opposed to a heraldic crest / Wappen).
 */
export function isPortraitAvatar(avatarId: string | null | undefined): boolean {
  const { id } = parseAvatarId(avatarId);
  if (!id) return false;
  const coaIndex = id.startsWith("coa-") ? parseInt(id.slice(4), 10) : NaN;
  return !isNaN(coaIndex) && coaIndex >= COA_PORTRAIT_START && coaIndex <= COA_COUNT;
}

/**
 * Returns a display name formatted with their title prefixed (e.g. "Lord MaxeLBerger")
 * if the avatarId contains a title.
 */
export function formatNameWithTitle(displayName: string | null | undefined, avatarId: string | null | undefined): string {
  const name = displayName || "Anonymous";
  const { title } = parseAvatarId(avatarId);
  return title ? `${title} ${name}` : name;
}

/**
 * Renders an avatar at (x, y) with the specified size.
 * Crest ids ("azure-lion-or") are composed procedurally; "coa-15".."coa-30"
 * are painted portrait avatars used directly. Legacy "coa-1".."coa-14"
 * (charge silhouettes) are rendered as a default crest with that charge.
 */
export function drawAvatar(
  scene: Phaser.Scene,
  x: number,
  y: number,
  size: number,
  avatarId: string | null | undefined,
  fallbackUserId?: string
): Phaser.GameObjects.Image {
  const { id: cleanId } = parseAvatarId(avatarId);
  let id = cleanId;
  const coaIndex = id && id.startsWith("coa-") ? parseInt(id.slice(4), 10) : NaN;
  const isPortrait = coaIndex >= COA_PORTRAIT_START;
  if (!isPortrait && coaIndex >= 1) {
    // Legacy silhouette pick from the old premium tab: show it as a crest charge.
    id = `gules-${CREST_CHARGE_NAMES[coaIndex - 1]}-or`;
  }

  const textureKey = isPortrait ? (id as string) : `avatar-${id || fallbackUserId || "anon"}`;

  // Generate the crest texture if it doesn't exist and it is not a preloaded premium image
  if (!isPortrait) {
    generateCrestFromId(scene, id, textureKey, fallbackUserId);
  }

  // Create the image
  const img = scene.add.image(x, y, textureKey);
  img.setOrigin(0.5);

  // Set scale based on size relative to actual texture width
  const tex = scene.textures.get(textureKey);
  const frame = tex ? tex.get() : null;
  const texWidth = frame ? frame.width : 192;
  const scale = size / texWidth;
  img.setScale(scale);

  return img;
}
